import React, { ReactNode, useEffect, useState } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { Box, Button, Stack } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNotifications } from 'modules/notification';
import { generateEquipmentName } from 'modules/equipment';
import { useCSDecision } from 'modules/creditSubmission';
import { ModalsContainer } from 'components/shared/Modals';
import { useHistory, useLocation } from 'react-router-dom';
import { creditAppSelectors } from 'redux/creditAppReducer';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { CreateProposalDTO, useProposal } from 'modules/proposals';
import { FormSectionHeader } from 'components/shared/FormSectionHeader';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { GET_CREDIT_SUBMISSION } from 'components/SubmissionDetailsTabs_V2/queries/queries';
import {
  CreateProposalFinanceExistingOptions,
  DeskingEquipmentFormSection,
  FinanceStructure,
} from 'modules/desking/shared';

import { useCalculate } from '../api';
import { TabsInSection } from './TabsInSection';
import { AddContingencyDialog } from './dialogs';
import { lenderDeskingDataShema } from '../model';
import { DeclineReasonOption } from '../../types';
import { lenderDeskingPanelMapper } from '../mappers';
import { AddEditDeclineReasonDialog } from '../../ui/dialogs';
import { DeclineForm } from '../../ui/decisionForm/DeclineForm';
import { useDeclineReasons } from '../../hooks/useDeclineReasons';
import { StyledLenderDeskingPanel } from './StyledLenderDeskingPanel';
import { useCreditSubmission } from '../../hooks/useCreditSubmission';
import { DecisionSection } from './sections/decision/DecisionSection';
import { LenderDeskingPanelApiContainer } from './LenderDeskingPanelApiContainer';
import { LenderDeskingPanelFormValues, LenderDeskingPanelProps, LenderDeskingSections } from '../types';
import { ActionsSection, PaymentOptionsSection, EligibleFinanceProgramsSection, ConditionsSection } from './sections';

const initialSectionView = Object.values(LenderDeskingSections).reduce(
  (acc, section) => {
    acc[section] = { isSectionOpen: true, isSectionCollapsed: true };
    return acc;
  },
  {} as Record<LenderDeskingSections, { isSectionOpen: boolean; isSectionCollapsed: boolean }>
);

const modalsWithKeys = {
  [ModalsKeys.AddContingencyDialog]: <AddContingencyDialog />,
  [ModalsKeys.AddEditDeclineReasonDialog]: <AddEditDeclineReasonDialog />,
};

export const LenderDeskingPanel = ({
  vo,
  cs,
  invoice,
  equipment,
  accountId,
  creditAppData,
  preSelectedValue,
  availableEmailOptions,
}: LenderDeskingPanelProps) => {
  const { replace } = useHistory();
  const location = useLocation();
  const { showNotification } = useNotifications();
  const { handleOpen: handleOpenConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const customerData = useSelector(creditAppSelectors.customerData);

  const { handleCreateProposalOnSubmissionApproval, isProposalCreating } = useProposal();
  const { handlePostCSDecision, isCSDecisionPosting } = useCreditSubmission();
  const { handleSubmitDecision, handleUpdateDecision, decisionSubmitting, decisionUpdating } = useCSDecision();

  const [sectionView, setSectionView] = useState(initialSectionView);
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);

  const {
    declineReasons,
    selectedOption: matchedDeclineOption,
    refetch: refetchCreditSubmissionDeclineReasons,
  } = useDeclineReasons({
    accountId,
    creditSubmissionDeclineReasonId: cs?.creditSubmissionDeclineReasonId,
  });

  const form = useForm<LenderDeskingPanelFormValues>({
    mode: 'all',
    resolver: zodResolver(lenderDeskingDataShema),
    defaultValues: lenderDeskingPanelMapper.initPanel({ invoice, equipment, cs, preSelectedValue }),
  });

  useEffect(() => {
    if (matchedDeclineOption) {
      form.setValue('creditSubmissionDeclineReason', matchedDeclineOption);
    }
  }, [matchedDeclineOption]);

  const [program, decision] = useWatch({
    control: form.control,
    name: ['financeQuote', 'submission.decision'],
  });

  const isDeclineReasonsShowing = ['803370001', '803370002'].includes(decision ?? '') && !!declineReasons.length;

  const { isCalculating, financePricing, setFinancePricing } = useCalculate(program, cs, invoice);

  const handleCreateProposalAndUpdateCS = async (
    proposalDTO: Omit<CreateProposalDTO, 'vendorOpportunityId' | 'creditSubmissionId'>,
    csDTO: Record<string, any>,
    notification?: { recipient: string; internalEmails: string[] }
  ) => {
    const isCSDecisionApproved = cs?.decision === 'Approved';

    await Promise.all([
      handlePostCSDecision(
        cs?.creditSubId,
        {
          masterCreditSubmissionId: cs.masterCreditSubmissionId ?? null,
          ...(!isCSDecisionApproved ? { decision: Number(decision) } : {}),
          ...csDTO,
        },
        () => ({
          awaitRefetchQueries: true,
          refetchQueries: [GET_CREDIT_SUBMISSION],
        })
      ),

      handleCreateProposalOnSubmissionApproval(
        {
          vendorOpportunityId: vo.vendorOpportunityId,
          creditSubmissionId: cs?.creditSubmissionId,
          ...proposalDTO,
        },
        notification
      ),
    ]).then(() => {
      replace({ hash: '#proposals', pathname: location.pathname });
    });
  };

  const handleSendToApplicant = async (formValues: LenderDeskingPanelFormValues) => {
    const createSendToApplicantDTO = lenderDeskingPanelMapper.processFormToCreateDto({
      invoice,
      formValues,
      creditAppData,
      isDraft: false,
      financePricing,
      customerData,
    });

    const postCSDecisionDto = lenderDeskingPanelMapper.processFormToPostCSDecisionPayload(
      formValues,
      cs.masterCreditSubmissionId
    );

    await handleCreateProposalAndUpdateCS(createSendToApplicantDTO, postCSDecisionDto, {
      recipient: 'applicant',
      internalEmails: formValues.actions.notifyUsers.map(({ email }) => email),
    });
  };

  const handleCreateFinanceDecision = async (formValues: LenderDeskingPanelFormValues) => {
    try {
      const isCSDecisionApproved = cs?.decision === 'Approved';

      const dto = lenderDeskingPanelMapper.processFormToCSDecisionDto(formValues);
      const postCSDecisionDto = lenderDeskingPanelMapper.processFormToPostCSDecisionPayload(
        formValues,
        cs.masterCreditSubmissionId
      );

      await handleSubmitDecision({ creditSubmissionId: cs.creditSubmissionId!, ...dto });
      await handlePostCSDecision(
        cs?.creditSubId,
        {
          ...postCSDecisionDto,
          ...(!isCSDecisionApproved ? { decision: Number(decision) } : {}),
        },
        () => ({
          awaitRefetchQueries: true,
          refetchQueries: [GET_CREDIT_SUBMISSION],
        })
      );

      replace({ hash: '#general', pathname: location.pathname });
    } catch (error) {
      logError(error);
    }
  };

  const handleUpdateFinanceDecision = async (formValues: LenderDeskingPanelFormValues) => {
    try {
      const latestDecision = cs?.decisions?.[cs.decisions.length - 1];

      if (!latestDecision) return;

      const dto = lenderDeskingPanelMapper.processFormToCSDecisionDto(formValues);
      const postCSDecisionDto = lenderDeskingPanelMapper.processFormToPostCSDecisionPayload(
        formValues,
        cs.masterCreditSubmissionId
      );

      await handleUpdateDecision(latestDecision.id, dto);
      await handlePostCSDecision(cs?.creditSubId, postCSDecisionDto, () => ({
        awaitRefetchQueries: true,
        refetchQueries: [GET_CREDIT_SUBMISSION],
      }));

      replace({ hash: '#general', pathname: location.pathname });
    } catch (error) {
      logError(error);
    }
  };

  const handleDecisionSave = async (value: string) => {
    try {
      const formValues = form.getValues();
      await handlePostCSDecision(
        cs?.creditSubId,
        {
          masterCreditSubmissionId: cs.masterCreditSubmissionId ?? null,
          decision: Number(value),
          notes: isDeclineReasonsShowing ? formValues.submission.notes : '',
          creditSubmissionDeclineReasonId: isDeclineReasonsShowing
            ? (formValues.creditSubmissionDeclineReason as DeclineReasonOption)?.value || null
            : null,
        },
        () => ({
          awaitRefetchQueries: true,
          refetchQueries: [GET_CREDIT_SUBMISSION],
        })
      );

      showNotification('Decision updated successfully');
    } catch (error) {
      console.error('Error saving decision:', error);
      showNotification('Error saving decision', { type: 'error' });
    }
  };

  const handleResetStructure = () => {
    setFinancePricing({});
    form.reset();
  };

  const isAllActionsLoading = isCSDecisionPosting || isProposalCreating;

  return (
    <>
      <LenderDeskingPanelApiContainer accountId={accountId}>
        {({ loading, financeProgramOptions, actions }) => {
          const handleApplyProgram = async (financeProgramId: string, tierName: string | null) => {
            const scoreCardResult = tierName ? { highestTierPassed: tierName } : cs?.scoreCardResult;
            const formValues = await actions.handleFetchFinanceProgram(financeProgramId, scoreCardResult);

            if (formValues) {
              const currentFormValues = form.getValues();

              form.reset({
                ...currentFormValues,
                financeQuote: {
                  ...currentFormValues.financeQuote,
                  ...formValues,
                  program: financeProgramId,
                  createProposalWithExistingProgram: CreateProposalFinanceExistingOptions.Yes,
                },
              });
            }
          };

          const renderNestedSectionByMode: Record<string, ReactNode> = {
            financeProgram: (
              <FinanceStructure
                financeAmount={invoice.invoiceTotal}
                financeProgramOptions={financeProgramOptions}
                isFPOptionsLoading={loading.isFPOptionsLoading}
                maxApprovalAmount={program.maxApprovalAmount ?? 0}
                netInvoiceAmount={Number(form.getValues('equipment.totalWholesaleCost'))}
                onFetchFinanceProgram={async programId =>
                  actions.handleFetchFinanceProgram(programId, cs?.scoreCardResult)
                }
              />
            ),
            conditions: <ConditionsSection />,
          };

          return (
            <StyledLenderDeskingPanel>
              <FormProvider {...form}>
                <DecisionSection
                  cs={cs}
                  isCSDecisionPosting={isCSDecisionPosting}
                  handleDecisionSave={handleDecisionSave}
                />

                <RenderWithCondition condition={isDeclineReasonsShowing}>
                  <DeclineForm<LenderDeskingPanelFormValues>
                    isEditable
                    required={false}
                    notesFieldName="submission.notes"
                    creditSubmissionDeclineReasons={declineReasons}
                    refetchCreditSubmissionDeclineReasons={refetchCreditSubmissionDeclineReasons}
                  />
                </RenderWithCondition>

                <RenderWithCondition condition={decision === '803370003'}>
                  <EligibleFinanceProgramsSection
                    vo={vo}
                    accountId={accountId}
                    creditSubmissionId={cs?.creditSubmissionId}
                    onApplyProgram={handleApplyProgram}
                  />

                  <RenderWithCondition condition={!!equipment.length}>
                    <DeskingEquipmentFormSection
                      name="Equipment"
                      isSectionDisabled
                      equipments={equipment}
                      value={LenderDeskingSections.EQUIPMENT}
                      isSectionOpen={sectionView[LenderDeskingSections.EQUIPMENT].isSectionCollapsed}
                      handleUpdateOpenStatus={(section, nextValue) => {
                        setSectionView(prev => ({
                          ...prev,
                          [section]: { ...prev[section], isSectionCollapsed: nextValue },
                        }));
                      }}
                      equipmentOptions={equipment.map(equipmentItem => ({
                        value: equipmentItem.equipmentId,
                        label: generateEquipmentName(equipmentItem),
                      }))}
                    />
                  </RenderWithCondition>

                  <Box className="row">
                    <Stack gap="0.5rem">
                      <FormSectionHeader
                        name="Structure"
                        value={LenderDeskingSections.STRUCTURE}
                        extraAction={
                          <Button size="small" onClick={handleResetStructure}>
                            Reset
                          </Button>
                        }
                      />

                      <TabsInSection
                        renderNestedSectionByMode={renderNestedSectionByMode}
                        configuration={[
                          { key: 'financeProgram', label: 'Finance' },
                          { key: 'conditions', label: 'Conditions' },
                        ]}
                      />
                    </Stack>

                    <PaymentOptionsSection
                      selectedTerm={selectedTerm}
                      isCalculating={isCalculating}
                      financePricing={financePricing}
                      onSelectOption={term => setSelectedTerm(prev => (prev === term ? null : term))}
                    />
                  </Box>

                  <ActionsSection
                    name="Send"
                    value={LenderDeskingSections.SEND}
                    decisionExist={!!cs?.decisions?.length}
                    availableEmailOptions={availableEmailOptions}
                    actions={{
                      createDecision: {
                        isLoading: decisionSubmitting,
                        isDisabled: !cs?.creditSubmissionId,
                        fn: () => form.handleSubmit(handleCreateFinanceDecision)(),
                      },
                      updateDecision: {
                        isLoading: decisionUpdating,
                        isDisabled: !cs?.creditSubmissionId || cs?.decision !== 'Approved',
                        fn: () => form.handleSubmit(handleUpdateFinanceDecision)(),
                      },
                      cancelChanges: {
                        isDisabled: isAllActionsLoading,
                        fn: () =>
                          handleOpenConfirmationModal({
                            title: 'Confirmation',
                            onConfirm: handleResetStructure,
                            message: 'Are you sure? All changes will be reset!',
                          }),
                      },
                      sendToApplicant: {
                        isLoading: isAllActionsLoading,
                        fn: () => form.handleSubmit(handleSendToApplicant)(),
                      },
                    }}
                  />
                </RenderWithCondition>
              </FormProvider>
            </StyledLenderDeskingPanel>
          );
        }}
      </LenderDeskingPanelApiContainer>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
};
